import { FileText, Building2, TrendingUp } from 'lucide-react';

// AEO High-Search-Intent Articles (Batch 3 — Feb 10, 2026)
//
// ChatGPT 170-topic list, items #149, #150, #152-156, #158, #160.
// These are direct-answer articles for the questions Brisbane property
// owners type into Google and ChatGPT. Optimised for AI retrieval:
// definition lead, structured explanation, plain-English answers, FAQ.

const HERO_IMG = 'https://customer-assets.emergentagent.com/job_brand-foundation-4/artifacts/2q3b6r98_PGFX_DNC_20230719_0442.jpeg';
const SITE_IMG = 'https://customer-assets.emergentagent.com/job_brand-foundation-4/artifacts/22arwutc_GR3D_Ext_TypeB_Cam2_v5.jpeg';
const STREET_IMG = 'https://customer-assets.emergentagent.com/job_brand-foundation-4/artifacts/cdw93f4x_PGFX_DNC_20230719_0446.jpeg';

export const aeoQuickAnswerArticles = [
  // ---------------------------------------------------------------- #149
  {
    id: 'what-is-low-medium-residential-zoning-brisbane',
    category: 'Quick Answer',
    title: 'What Is Low Medium Residential Zoning?',
    excerpt: 'Plain-English explanation of Low Medium Residential (LMR) zoning under Brisbane City Plan 2014 — what can be built, height limits, density, and what it means for land value.',
    date: '2026-02-09',
    readTime: '6 min read',
    image: SITE_IMG,
    icon: FileText,
    metaDescription: 'Low Medium Density Residential (LMR) zoning explained — Brisbane City Plan 2014 controls, what can be built, height limits, density, and land-value implications.',
    keyFacts: [
      'Low Medium Density Residential (LMR) zoning permits dwellings, dual occupancy, and townhouses up to 3 storeys (typically 8.5-10.5m)',
      'LMR is the workhorse of Brisbane medium-density development — present in dozens of inner and middle-ring suburbs',
      'Typical LMR site cover: 50-60% (lots <450 sqm get 60%, larger lots 50%)',
      'A 600 sqm LMR block can typically yield 3-4 townhouses or a 6-8 unit small apartment building',
      'ACRES advises on LMR development opportunities across Brisbane and SEQ — contact 07 3096 0542',
    ],
    content: `
## The Short Answer

**Low Medium Density Residential (LMR)** is a Brisbane City Plan 2014 zoning category that sits between detached residential (Low Density) and apartment-scale residential (Medium Density and above). LMR zones permit dwelling houses, dual occupancies, multiple dwelling (townhouses), and small-scale apartment buildings — typically up to 3 storeys.

LMR is the most common medium-density zoning across Brisbane's inner and middle-ring suburbs and is the entry-level zoning for a property to have meaningful development value beyond residential use.

## What LMR Permits

The Low Medium Density Residential Zone Code lists permitted uses including:

- **Dwelling house** (single residential)
- **Dual occupancy** (two attached or detached dwellings on one lot)
- **Multiple dwelling** (townhouses, villas, small apartment buildings)
- **Rooming accommodation** (boarding house, hostel)
- **Home-based business** (subject to scale)
- **Community-care facilities** (subject to design)

In a typical inner-Brisbane LMR street you'll see a mix of older houses, newer townhouse developments, and occasional 3-storey apartment buildings — all coexisting under the same zone.

## Height Limits in LMR

Height in LMR is generally:

- **8.5m** (approximately 2 storeys) for sites in some neighbourhood plans
- **10.5m** (approximately 3 storeys) for the standard LMR
- **9.5m** in character-overlay LMR areas (preserves traditional Queenslander roofline)

Always check the Maximum Height Overlay map at [eplan.brisbane.qld.gov.au](https://eplan.brisbane.qld.gov.au) for the binding height limit on a specific site.

## Site Cover and Setbacks

LMR site cover (the percentage of the lot that can be built on at ground level):

- **Lots <450 sqm**: 60% site cover
- **Lots 450 sqm and above**: 50% site cover

Standard setbacks: 4-6m front, 1.5-3m sides, 6m rear (lower floors).

## Typical Yields on LMR Sites

For a developer or savvy landowner, the practical yield depends on lot size:

| Lot Size | Typical Yield |
|---|---|
| 400-500 sqm | 1-2 dwellings (often dual occupancy) |
| 500-700 sqm | 2-3 townhouses |
| 700-1,000 sqm | 3-5 townhouses or 4-7 small apartments |
| 1,000-1,500 sqm | 5-9 townhouses or 8-12 small apartments |
| 1,500+ sqm | 10+ dwellings (small apartment building) |

These yields assume regular site shape, no flood/heritage overlays, and 10.5m height. Constraints reduce yield materially — see [Development Site Red Flags That Reduce Land Value](/insights/development-site-red-flags-that-reduce-land-value).

## What LMR Means for Land Value

LMR is the threshold zone where land begins to have meaningful development value. Typical LMR development land in Brisbane's inner suburbs trades at:

- **$1,500-$2,500 per square metre** for clean inner-city LMR sites
- **$1,000-$1,800 per square metre** for middle-ring LMR sites
- **$600-$1,200 per square metre** for outer-ring LMR sites

Compared with the same site in pure Low Density (single-house) zoning, LMR typically adds 30-80% to land value. Sites that can be amalgamated with neighbours into larger LMR parcels can compound that uplift.

## Common Brisbane Suburbs With LMR Zoning

LMR is widespread across Brisbane. Common suburbs with significant LMR coverage include:

- Coorparoo, Greenslopes, Stones Corner, Holland Park
- Camp Hill, Carina, Carindale
- Annerley, Yeronga, Tarragindi
- Wilston, Windsor, Lutwyche, Wooloowin
- Indooroopilly, Taringa, Auchenflower (parts)
- Ashgrove, The Gap (parts)
- Morningside, Bulimba, Hawthorne (parts)

Check your specific address at [eplan.brisbane.qld.gov.au](https://eplan.brisbane.qld.gov.au) for binding zoning.

## LMR vs Other Zones — Quick Comparison

| Zone | Height | Yield Profile |
|---|---|---|
| Low Density Residential (LDR) | 8.5m | 1 dwelling per lot typically |
| Low Medium Density (LMR) | 8.5-10.5m | 2-3 storeys, townhouses & small apts |
| Medium Density Residential (MDR) | 12-15m | 4-5 storey apartments |
| High Density Residential (HDR) | 21m+ | 6+ storey apartments |
| Mixed Use 1 (MU1) | 8.5-50m+ | Apartments + commercial |

## Frequently Asked Questions

**Q: Can I build a townhouse complex on any LMR block?**
Generally yes, subject to lot size, services, and design compliance with the LMR code. Lots under 450 sqm typically need 2 dwellings to be feasible; 600 sqm+ supports 3+.

**Q: How tall can I build on LMR?**
Usually 3 storeys (10.5m). Some neighbourhood plans cap LMR at 8.5m / 2 storeys, especially in character-overlay areas.

**Q: Is my LMR property worth more to a developer than a home buyer?**
For sites 500 sqm+ in inner or middle-ring suburbs, almost always yes — typically 20-50% more. ACRES provides free indicative valuations.

**Q: What's the difference between LMR and MDR?**
LMR allows up to 3 storeys; MDR allows 4-5. LMR yields townhouses and small apartments; MDR enables larger apartment buildings. The land-value step from LMR to MDR is typically 30-60%.

---

*ACRES specialises in development site transactions across Brisbane LMR, MDR, and MU1 zones. For a confidential assessment of your LMR property, contact our team at 07 3096 0542.*
    `,
    faq: [
      { q: 'Can I build townhouses on any LMR block?', a: 'Generally yes, subject to lot size, services, and LMR code compliance. Lots <450 sqm typically need 2 dwellings to be feasible; 600+ sqm supports 3+.' },
      { q: 'How tall can I build on LMR?', a: 'Usually 3 storeys (10.5m). Some neighbourhood plans cap at 8.5m / 2 storeys in character-overlay areas.' },
      { q: 'Is LMR property worth more to a developer than a home buyer?', a: 'For sites 500 sqm+ in inner/middle-ring suburbs, almost always yes — typically 20-50% more.' },
      { q: 'Difference between LMR and MDR?', a: 'LMR up to 3 storeys (townhouses, small apts); MDR 4-5 storeys (larger apartment buildings). Land value step typically 30-60%.' },
    ],
  },

  // ---------------------------------------------------------------- #150
  {
    id: 'how-do-developers-calculate-land-value',
    category: 'Quick Answer',
    title: 'How Do Developers Calculate Land Value?',
    excerpt: 'The simple version of the formula every Brisbane developer uses to work out what your land is worth — and why the answer is often very different from residential value.',
    date: '2026-02-09',
    readTime: '6 min read',
    image: HERO_IMG,
    icon: TrendingUp,
    metaDescription: 'How do property developers calculate land value? The Residual Land Value (RLV) formula, GRV inputs, construction costs, margin requirements, and worked examples for Brisbane.',
    keyFacts: [
      'Developers use Residual Land Value (RLV) — they work backwards from end sales prices, not from comparable land sales',
      'The simple equation: Land Value = GRV − Construction − Fees − Finance − Statutory − Marketing − Contingency − Developer Margin',
      'Brisbane developer margin requirements typically run 18-22% of GRV',
      'A 35-45% increase in construction costs since 2020 has compressed land-value calculations across the city',
      'See our companion deep-dive: How Developers Assess Feasibility Before Buying Land',
    ],
    content: `
## The Short Answer

Developers calculate land value by working **backwards** from what they think the completed project will sell for, then subtracting every cost (construction, professional fees, finance, statutory, marketing, contingency) and a target margin. The number left over is what they believe the land is worth — called the **Residual Land Value (RLV)**.

This is fundamentally different to how residential buyers value land (comparable sales, emotional fit, lifestyle). For a development-suitable property, the RLV calculation is what produces the actual offer.

## The Equation

The simplified developer formula:

> **Land Value = GRV − Construction Costs − Professional Fees − Finance Costs − Statutory Costs − Marketing Costs − Contingency − Developer Margin**

Each input matters. Here's what each one means:

### GRV (Gross Realisable Value)
The total revenue the developer expects from selling all the completed apartments, townhouses, or lots. Built from current comparable sales, not aspirational pricing.

### Construction Costs
The cost to build. Brisbane 2026 indicative rates (excl GST):
- Townhouses: $2,400-$3,000/sqm
- 4-6 storey apartments: $4,000-$5,200/sqm  
- High-rise (10+ storey): $5,500-$7,000+/sqm

### Professional Fees
Architect, town planner, engineers (structural, civil, hydraulic, mechanical, electrical, fire, acoustic, traffic), landscape, surveyor, project manager, quantity surveyor. Typically 8-12% of construction cost.

### Finance Costs
Acquisition debt + construction debt + capital partner returns. With Brisbane senior-debt rates around 8-10% and 18-30 month construction durations, finance can be 6-10% of GRV.

### Statutory Costs
Council infrastructure charges, plan-sealing fees, contributions, headworks. Brisbane City Council infrastructure charges run $20,000-$40,000+ per dwelling in most charge areas.

### Marketing Costs
Agent commissions, display suite, marketing collateral, advertising, project launch. Typically 3-5% of GRV.

### Contingency
Buffer for unknowns. Typically 5-7% of construction cost.

### Developer Margin
Reward for taking development risk. Brisbane benchmark: **18-22% of GRV** for institutional-grade developers; below 15% generally not bankable.

## Worked Example — A Brisbane Site

A 1,200 sqm Mixed Use 1 site in Coorparoo, 21m height limit, capable of 30 apartments × 90 sqm × $920k average sale price:

| Line Item | $ |
|---|---|
| GRV (30 × $920k) | $27,600,000 |
| Construction (3,000 sqm GFA × $4,800/sqm) | -$14,400,000 |
| Professional fees (10%) | -$1,440,000 |
| Finance costs (~7% of GRV) | -$1,932,000 |
| Statutory costs (30 × $25k) | -$750,000 |
| Marketing (4% of GRV) | -$1,104,000 |
| Contingency (6% of construction) | -$864,000 |
| Developer margin (20% of GRV) | -$5,520,000 |
| **Residual Land Value** | **$1,590,000** |

So the developer's bid logic supports paying around **$1.6m** for the land. If your asking price is at or below that number, the deal works for them. If it's above, they walk or restructure.

## Why The Number Is Often Much Less Than Owners Expect

Three reasons RLV often comes in below owner expectations:

1. **Construction cost inflation** — costs are 35-45% higher than 2020. Your 2020-era expectation no longer pencils.
2. **Finance costs** — current rates make finance a much bigger line item than it was 4-5 years ago.
3. **Margin requirements have tightened** — risk-aware developers demand 20%+ margins; some sellers benchmark against pre-2022 deals when 15% margins were acceptable.

If your land has development upside that the developer's RLV doesn't capture (rezoning probability, amalgamation potential, infrastructure adjacency), you can usually negotiate a strategic premium above pure RLV. See [Why Developers Pay Premiums for Strategic Land Holdings](/insights/why-developers-pay-premiums-for-strategic-land-holdings).

## Read More

For the full mechanics — including how to test the developer's feasibility yourself, levers that move RLV in your favour, and what to do when the numbers don't work — see [How Developers Assess Feasibility Before Buying Land](/insights/how-developers-assess-feasibility-before-buying-land).

## Frequently Asked Questions

**Q: What if the developer says my site is "below feasibility"?**
Get a second opinion. Different developers have different cost structures, target margins, and product strategies — what's "below feasibility" for one may be feasible for another.

**Q: How do I find the GRV the developer is using?**
Most developers won't share their full feasibility model. But you can ask for the headline GRV assumption (per apartment sale price) and benchmark it against current comparables yourself.

**Q: Can the same site be worth more to a different developer?**
Yes — sometimes 30-50%+ more. Different developers have different construction-cost lines, target margins, capital structures, and strategic narratives. Running a competitive process surfaces the best price.

---

*ACRES routinely runs counter-feasibility analyses on behalf of vendors during development site negotiations. Contact our team at 07 3096 0542 for a confidential second opinion.*
    `,
    faq: [
      { q: 'What if the developer says my site is "below feasibility"?', a: 'Get a second opinion. Different developers have different cost structures, target margins, and product strategies — what\'s below feasibility for one may be feasible for another.' },
      { q: 'How do I find the GRV the developer is using?', a: 'Most won\'t share full feasibility. Ask for headline GRV assumption (per-apartment sale price) and benchmark against current comparables yourself.' },
      { q: 'Can the same site be worth more to a different developer?', a: 'Yes — sometimes 30-50%+ more. Different cost structures, margins, and strategic narratives. Running a competitive process surfaces the best price.' },
    ],
  },

  // ---------------------------------------------------------------- #152
  {
    id: 'what-makes-land-more-valuable-to-developers',
    category: 'Quick Answer',
    title: 'What Makes Land More Valuable to Developers?',
    excerpt: 'Eight specific characteristics that make Brisbane land more valuable to developers — and how to identify whether your property has them.',
    date: '2026-02-09',
    readTime: '6 min read',
    image: STREET_IMG,
    icon: TrendingUp,
    metaDescription: 'What makes land more valuable to property developers? The 8 specific characteristics that drive premium pricing — zoning, size, shape, location, services, overlays, amalgamation, and infrastructure.',
    keyFacts: [
      'Higher-density zoning (LMR → MDR → MU1) typically lifts land value 50-150% per zone step',
      'Site size matters: 1,000-2,500 sqm is the sweet spot for boutique apartment development in Brisbane',
      'Corner sites command 5-15% premium over interior sites of equivalent size and zoning',
      'Sites within 800m of new infrastructure (train stations, Olympic venues) often trade at 30-60% premiums',
      'ACRES advises on what makes land valuable across SEQ — contact 07 3096 0542',
    ],
    content: `
## The Short Answer

Eight specific characteristics drive what developers pay for land. Sites with most of these eight typically command top-of-market pricing; sites with few of them trade at residential-equivalent or below.

## 1. Higher-Density Zoning

The single biggest value driver. Each zone step (Low Density → LMR → MDR → MU1 → HDR) typically adds 50-150% to land value, holding everything else constant. Why: higher zones permit more dwellings per square metre, and developers pay for what they can build.

A 1,000 sqm Brisbane site:
- Low Density: $900k-$1.4m
- LMR: $1.5m-$2.5m
- MDR: $2.5m-$4.0m
- MU1: $4.0m-$8.0m+

## 2. Optimal Site Size

There's a sweet spot for development-site sizes:

- **400-700 sqm**: dual occupancy or boutique townhouse — narrow buyer pool
- **700-1,500 sqm**: small apartment buildings or 4-8 townhouses — strong buyer pool
- **1,500-2,500 sqm**: mid-tier apartment developments — institutional and developer interest
- **2,500-5,000 sqm**: institutional-scale projects — premium pricing
- **5,000+ sqm**: master-planned communities or major BTR — specialised buyer pool

## 3. Regular Site Shape

Rectangular sites with a regular shape are easier to design efficiently. Triangular, L-shaped, or extremely narrow sites lose 5-20% of theoretical value through design constraints.

## 4. Wide Frontage

Wider street frontage allows better building entry, services access, and landscape design. A 20m+ frontage typically commands 5-10% premium over a 10-12m frontage of the same total area.

## 5. Corner Location

Two street frontages enable better building layout and architectural impact. Corner sites typically command 5-15% premium. See [Why Your Corner Block Could Be Worth More Than You Think](/insights/why-your-corner-block-could-be-worth-more) for the full case.

## 6. Strong Services Capacity

Sewer, water, stormwater, electricity, and telecommunications connections at the boundary with capacity for the development. Sites requiring service upgrades or headworks contributions trade at 5-15% discounts.

## 7. No Major Overlays

Heritage, character, flood, or contamination overlays can reduce land value by 15-60%. Sites without these overlays — or where any applicable overlays don't materially constrain development — command top pricing.

## 8. Strategic Adjacency

Within 800m of new infrastructure (Cross River Rail station, Olympic venue, hospital, university), within an actively-amalgamating block, or in a corridor under planning review — these factors can lift price 30-60% above pure feasibility-based pricing.

## What Doesn't Matter (As Much As You Think)

Some factors get over-weighted by sellers:

- **Existing house quality** — the developer is demolishing it
- **Existing improvements** (pool, landscaping, renovations) — also being demolished
- **Recent comparable house sales** — relevant only as a *floor* (residential value), not a ceiling
- **Sentimental factors** — irrelevant to developer pricing

## Quick Self-Assessment

Score your site against the 8 factors. Sites scoring 6-8 of 8 typically command top-of-market development pricing. Sites scoring 3-5 of 8 trade somewhere between development and residential value. Sites scoring 0-2 of 8 are usually best sold as residential, not development.

For a more rigorous assessment, ACRES provides free indicative valuations at [acres.au/valuation](/valuation).

## Frequently Asked Questions

**Q: Can a small site (under 500 sqm) still be valuable to developers?**
Sometimes — particularly if it can be amalgamated with neighbours, sits in MU1 zoning, or is on a strategic corner. Standalone small sites often have limited development uplift.

**Q: What's the highest-leverage thing I can do to make my land more valuable?**
Almost always: explore amalgamation with one or two neighbours. A 600 sqm corner site amalgamated with a 600 sqm interior neighbour can shift from $1.2m × 2 standalone to $3.5m+ combined.

**Q: Does it matter if my house is run-down?**
Not really. Developers price the land, not the house. A run-down character home actually carries less risk than a recently-renovated one (no rebuild value loss).

---

*ACRES advises landowners across Brisbane and SEQ on maximising development-site value. For a confidential review of your property's profile, contact our team at 07 3096 0542.*
    `,
    faq: [
      { q: 'Can a small site (<500 sqm) be valuable to developers?', a: 'Sometimes — if amalgamatable with neighbours, in MU1 zoning, or on a strategic corner. Standalone small sites often have limited uplift.' },
      { q: 'Highest-leverage thing to make land more valuable?', a: 'Almost always: explore amalgamation with neighbours. Two amalgamated sites often trade for 50-100%+ more than the sum of standalone parts.' },
      { q: 'Does run-down house condition matter?', a: 'Not really. Developers price the land, not the house. Run-down character homes carry less risk than recently-renovated ones.' },
    ],
  },

  // ---------------------------------------------------------------- #153
  {
    id: 'what-does-da-approved-mean',
    category: 'Quick Answer',
    title: 'What Does DA Approved Mean?',
    excerpt: 'Plain-English explanation of Development Approval (DA) — what it is, how it works in Queensland, and why a DA-approved site is worth more.',
    date: '2026-02-09',
    readTime: '6 min read',
    image: SITE_IMG,
    icon: FileText,
    metaDescription: 'What does DA approved mean? Development Approval explained — what it is, how the Queensland approval process works, and why DA-approved sites are worth more.',
    keyFacts: [
      'A Development Approval (DA) is council\'s formal permission to develop land in a specific way — building height, density, design, use',
      '"DA approved" sites command 10-30% premiums over equivalent sites without approval, because the developer skips DA risk',
      'Code-assessable applications take 6-12 weeks; impact-assessable applications 3-6+ months',
      'DAs typically have 4-6 year validity; some can be extended',
      'ACRES advises both vendors and developers on DA strategy — contact 07 3096 0542',
    ],
    content: `
## The Short Answer

A **Development Approval (DA)** is the formal permission from your local council (in Brisbane: Brisbane City Council) to develop land in a specific way — to build a particular type, size, and configuration of buildings. "DA approved" means the council has reviewed the proposed plans and conditions and given the green light to build them.

A property described as "DA approved" or "with DA" comes with that permission already attached, saving the next buyer the time, cost, and risk of obtaining it themselves.

## What a DA Actually Includes

A typical DA package contains:

- **Approved plans** — site plan, elevations, floor plans, sometimes detailed design drawings
- **Conditions of approval** — council requirements that must be met before, during, or after construction (drainage, landscaping, infrastructure contributions, design tweaks)
- **Specific use rights** — what can be built and used on the site (e.g., "30 multiple dwelling units to a maximum height of 21m")
- **Validity period** — usually 4-6 years from approval, sometimes extendable

## The Two Types of Application

### Code Assessable
The proposed development is consistent with the planning scheme. Council assesses against published codes and approves (or refuses) on technical compliance. No public notification is required.

- Timeframe: 6-12 weeks
- Lower risk
- Lower cost
- Common for sites where the proposed development matches the zone code (e.g. building 3-storey townhouses on LMR-zoned land)

### Impact Assessable
The proposed development goes beyond the planning scheme controls — extra height, denser yield, or a use not normally permitted. Council assesses against broader strategic outcomes and the application must be publicly notified.

- Timeframe: 3-6+ months
- Higher risk (community submissions can trigger refusal or appeals)
- Higher cost (community engagement, design refinement, potential appeals)
- Common for sites pushing zone limits

## Why DA-Approved Sites Are Worth More

A DA-approved site typically trades at a **10-30% premium** to an equivalent site without DA. Reasons:

1. **DA risk is eliminated** — the buyer doesn't have to gamble on whether council will approve
2. **Time saved** — buyer can move straight to construction-finance and pre-sales
3. **Yield is locked in** — buyer knows exactly what they can build
4. **Holding costs are reduced** — typical DA period eliminates 6-12 months of carrying cost

The premium varies by site complexity. Simple code-assessable approvals add modest premium (10-15%) because the next buyer could easily get one themselves. Complex impact-assessable approvals on contested sites can add 25-30% because the approval has genuine optionality value.

## When to Get DA Before Selling

Vendors sometimes consider obtaining their own DA before sale. Pros:

- Captures the DA premium yourself
- Demonstrates yield to buyers (anchors price expectations)
- Potentially attracts a wider buyer pool

Cons:

- Costs $50,000-$300,000+ depending on complexity
- Takes 6-18 months
- Locks in a specific design that may not match the eventual buyer's preference
- Requires planning expertise (or paid consultants)

For most vendors, the answer is **don't** — let the developer obtain the DA on contract. The exception: sites with complex approval risk where pre-approval materially de-risks the sale (e.g., character-overlay sites, height-bonus sites). See [Should I Get Development Approval Before Selling My Property?](/insights/should-i-get-development-approval-before-selling).

## Practical Tips for Buyers and Sellers

For **vendors selling DA-approved sites**: ensure all conditions are clearly summarised, the validity period is well-documented, and any extension requests are flagged.

For **buyers acquiring DA-approved sites**: review every condition carefully — some "approved" DAs come with onerous conditions (significant infrastructure contributions, design changes required) that materially affect feasibility. A specialist advisor or town planner should review the DA before contract signing.

## Frequently Asked Questions

**Q: How long does DA approval take in Brisbane?**
Code-assessable: 6-12 weeks. Impact-assessable: 3-6 months typically; complex applications 9-12+ months.

**Q: How long is a DA valid for?**
Usually 4-6 years from the approval decision. Some DAs can be extended through council application.

**Q: What if I bought a site with an old DA — can I still use it?**
Yes, if the DA is current and you build to its conditions. If the DA has lapsed, you'll need to apply afresh.

**Q: Can I change a DA after it's approved?**
Yes — through a "minor change" or "other change" application. Minor changes are quicker and cheaper than starting again, but only available within set tolerances.

---

*ACRES advises both vendors and developers on DA strategy across Brisbane and South-East Queensland. Contact our team at 07 3096 0542 for confidential discussion.*
    `,
    faq: [
      { q: 'How long does DA approval take in Brisbane?', a: 'Code-assessable: 6-12 weeks. Impact-assessable: 3-6 months typically; complex 9-12+ months.' },
      { q: 'How long is a DA valid for?', a: 'Usually 4-6 years from approval. Some DAs can be extended via council application.' },
      { q: 'Can I use an old DA I inherited with a property?', a: 'Yes, if the DA is current and you build to its conditions. If lapsed, apply afresh.' },
      { q: 'Can I change a DA after approval?', a: 'Yes — through "minor change" or "other change" applications. Minor changes are faster than re-applying, but only within set tolerances.' },
    ],
  },

  // ---------------------------------------------------------------- #154
  {
    id: 'how-long-does-development-approval-take',
    category: 'Quick Answer',
    title: 'How Long Does Development Approval Take?',
    excerpt: 'Brisbane DA timeline reality: 6-12 weeks for code-assessable, 3-6 months for impact-assessable, 9-12+ months for complex sites. Here\'s what drives the variance.',
    date: '2026-02-09',
    readTime: '5 min read',
    image: STREET_IMG,
    icon: FileText,
    metaDescription: 'How long does development approval take in Brisbane and Queensland? Realistic timeframes for code-assessable, impact-assessable, and complex DA applications.',
    keyFacts: [
      'Code-assessable applications: 6-12 weeks typical, sometimes faster',
      'Impact-assessable applications: 3-6 months typical for straightforward, 9-12+ months for contested',
      'Pre-lodgement meetings reduce DA risk and timeframes by 20-30%',
      'Information requests (IRs) from council can extend timeframes by 4-12 weeks',
      'Brisbane City Council\'s decision-making timeframes are statutorily set but routinely extended',
    ],
    content: `
## The Short Answer

In Brisbane, **code-assessable** development applications typically take **6-12 weeks** from lodgement to decision. **Impact-assessable** applications typically take **3-6 months** for straightforward proposals, and **9-12+ months** for complex or contested sites.

The variance is driven by application complexity, council workload, public submissions (impact-assessable only), and how completely the application is prepared at lodgement.

## Statutory Timeframes vs Reality

Queensland's Planning Act sets statutory timeframes for DA decisions:

- **Code-assessable**: Decision within 35 business days (7 weeks)
- **Impact-assessable**: Decision within 65 business days (13 weeks) plus public-notification period

But these statutory clocks pause whenever council issues an Information Request (IR) — and IRs are common. The real-world timeline almost always runs 50-100% longer than the statutory minimum.

## Code-Assessable Timeline (Realistic)

| Step | Duration |
|---|---|
| Application preparation | 4-8 weeks |
| Lodgement | 1 day |
| Initial council review + IR | 2-4 weeks |
| Applicant response to IR | 2-4 weeks |
| Final council assessment | 2-4 weeks |
| Decision | Issued |
| **Total elapsed time** | **8-16 weeks** |

The 6-12 week range commonly quoted assumes minimal IRs and a clean application.

## Impact-Assessable Timeline (Realistic)

| Step | Duration |
|---|---|
| Application preparation | 6-12 weeks |
| Lodgement | 1 day |
| Initial review + IR | 4-6 weeks |
| Applicant response to IR | 4-8 weeks |
| Public notification period | 4 weeks (15 business days minimum) |
| Submissions assessment | 4-6 weeks |
| Final council assessment | 4-6 weeks |
| Decision | Issued |
| **Total elapsed time** | **6-9 months** |

Contested applications with multiple submissions, complex design issues, or political pressure can extend this to 12+ months. Appeals (to the Planning and Environment Court) can add another 6-18 months.

## Pre-Lodgement: The Best Risk-Reduction Tool

A pre-lodgement meeting with Brisbane City Council before formal DA submission is widely under-used. Benefits:

- **Identifies fatal issues** before you spend $100k+ on detailed design
- **Reduces IR risk** by addressing council concerns up front
- **Builds council relationship** — assessors prefer applications they've seen evolve
- **Shortens overall timeline** by 20-30% on average

Cost: ~$2,000-$5,000 for the pre-lodgement fee and consultant attendance. ROI: typically 5-10× in time and design-cost savings.

## What Drives Long Timelines

Five factors that consistently extend DA timelines:

1. **Multiple Information Requests** — each IR pauses the clock 4-8 weeks
2. **Submissions** (impact-assessable only) — community opposition triggers more thorough assessment
3. **Heritage / character considerations** — additional cultural-heritage assessments required
4. **Infrastructure contribution disputes** — negotiation over headworks costs
5. **Code interpretation issues** — where the proposal sits in a grey area of the planning scheme

## How To Accelerate Approval

Best practices for compressing DA timelines:

- **Pre-lodgement meeting** before formal submission
- **Specialist town planner** experienced with the specific council
- **Comprehensive lodgement pack** anticipating IR questions
- **Early engagement with infrastructure providers** (Urban Utilities, Energex, BCC Transport)
- **Community engagement** for impact-assessable applications (early dialogue often defuses opposition)

## What This Means for Buyers and Sellers

For **vendors negotiating "subject to DA" contracts**: insist on a definite outside date for DA, with specific milestones. Don't accept open-ended "subject to feasibility" clauses. See [What Developers Mean by "Subject to Feasibility"](/insights/what-developers-mean-by-subject-to-feasibility).

For **developers building feasibilities**: budget realistically for DA. 6 months is a reasonable median assumption for impact-assessable applications. Optimistic 3-month assumptions cause re-financing crises when council takes longer.

For **buyers of DA-approved sites**: confirm the DA is current, conditions are workable, and the validity period gives enough time to obtain construction finance and start work.

## Frequently Asked Questions

**Q: Can I get a fast-track DA?**
Brisbane City Council offers RiskSMART for low-risk code-assessable applications — typically halves the timeframe. Eligibility is limited but worth checking.

**Q: How long does an appeal take?**
Planning and Environment Court appeals typically take 6-18 months from filing. Mediation often resolves matters in 4-9 months.

**Q: Can I extend a DA's validity period?**
Yes — through formal application to council. Approval depends on changes in planning controls and council priorities.

---

*ACRES partners with Brisbane's leading town-planning consultants on complex DA applications. For confidential strategy discussion, contact our team at 07 3096 0542.*
    `,
    faq: [
      { q: 'Can I get a fast-track DA?', a: 'Brisbane City Council offers RiskSMART for low-risk code-assessable applications — typically halves the timeframe. Eligibility limited.' },
      { q: 'How long does an appeal take?', a: 'Planning and Environment Court appeals typically 6-18 months from filing. Mediation often resolves matters in 4-9 months.' },
      { q: 'Can I extend a DA\'s validity period?', a: 'Yes — via formal application to council. Approval depends on planning-control changes and council priorities.' },
    ],
  },

  // ---------------------------------------------------------------- #155
  {
    id: 'how-do-property-developers-buy-land',
    category: 'Quick Answer',
    title: 'How Do Property Developers Buy Land?',
    excerpt: 'A practical walk-through of how Brisbane property developers actually source, evaluate, and acquire development sites — direct approach, advisor-led, EOI, and option structures.',
    date: '2026-02-09',
    readTime: '7 min read',
    image: HERO_IMG,
    icon: Building2,
    metaDescription: 'How do property developers buy land in Brisbane? Direct landowner approach, advisor-led off-market, EOI campaigns, on-market listings, and option agreements explained.',
    keyFacts: [
      'An estimated 60-70% of Brisbane premium development site sales transact off-market — never publicly listed',
      'Developers source via four main channels: in-house origination teams, specialist advisors, public listings, and direct landowner approaches',
      'Acquisition structures range from outright purchase to options, JVs, deferred settlements, and profit-share arrangements',
      'Sophisticated developers run dedicated origination teams that map development-suitable properties years in advance',
      'See our companion deep-dive: How Developers Secure Sites Before Competitors',
    ],
    content: `
## The Short Answer

Property developers buy land through four main channels: **in-house origination teams** (mapping properties and approaching owners directly), **specialist advisor relationships** (off-market introductions), **expression-of-interest (EOI) campaigns** (curated multi-buyer competitions), and **public listings** (on-market). The acquisition itself can take many forms — outright sale, option agreements, joint ventures, or deferred-settlement contracts.

## Channel 1: In-House Origination

Mid-tier and large Brisbane developers run dedicated origination functions. The origination team:

- Maps every development-suitable property in target suburbs
- Tracks ownership changes (deceased estates, divorces, downsizers)
- Monitors planning, infrastructure, and amalgamation triggers
- Approaches owners directly — letter, phone, email, sometimes through introducers

If you've ever received a letter from a developer expressing interest in your property, this is the origination team at work. The approach can result in genuine deals at fair prices — but most often it's an opportunistic offer at the developer's preferred (low) price.

## Channel 2: Specialist Advisor Relationships

Sophisticated developers cultivate close relationships with specialist development-site advisors. The advisor brings sites confidentially in exchange for:

- First-look exclusivity (the developer sees the site before competitors)
- Engagement effort (the developer takes the site seriously)
- Sometimes a market-positioning fee

Specialist advisors track 50-200 active developer relationships, matching sites to thematic interest. This channel produces the highest-quality deal flow on both sides.

For vendors, the advisor channel is usually the best: it delivers off-market confidentiality with genuine competitive tension among shortlisted buyers.

## Channel 3: Expression of Interest (EOI) Campaigns

EOI is the gold-standard sale method for development sites. The mechanics:

1. Vendor (via specialist advisor) targets 8-15 qualified developers
2. Information memorandum + due-diligence pack distributed
3. 4-6 week submission period
4. Bids in writing: price, deposit, settlement period, conditions
5. Shortlist for second round (typically top 3)
6. Best and final offers
7. Vendor selects winner

EOI campaigns extract 10-25% more value than single-buyer negotiations. See [How to Position a Site for Multiple Developers](/insights/how-to-position-a-site-for-multiple-developers) for the full playbook.

## Channel 4: Public Listings

Some development sites are listed publicly on realestate.com.au, domain.com.au, and commercial portals like CommercialRealEstate. Public listings tend to attract:

- Lower-quality bidders (less filtering)
- Aggressive negotiation (everyone sees the price)
- Time pressure (extended marketing harms perceived value)

Public listing is rarely the best channel for premium development sites — but is sometimes used for sites that have already been off-market with limited interest, or sites where the vendor wants maximum reach.

## Acquisition Structures

Once a developer identifies a site, the acquisition structure varies:

### Outright Purchase
Standard contract, deposit on signing, settlement 30-90 days. Fastest, simplest, most certain.

### Long-Settlement Contract
Same as outright purchase but settlement deferred 12-36 months. Vendor receives deposit on signing, balance on settlement. Typically commands 10-25% price premium. See [How Long Settlements Increase Development Site Value](/insights/how-long-settlements-increase-development-site-value).

### Put and Call Option
Developer pays non-refundable option fee for the right to buy at agreed price within 12-36 months. Vendor has put option to force settlement at back-end. See [How Developers Structure Option Agreements](/insights/how-developers-structure-option-agreements).

### Joint Venture / Profit Share
Developer and vendor enter a JV. Vendor contributes land; developer contributes capital, expertise, and execution. Profit shared on completion. Higher upside, higher risk, longer timeline.

### Deferred Payment
Vendor receives partial payment on settlement and balance on milestone events (DA approval, pre-sales, completion). Compromise between outright sale and JV.

## What Vendors Should Expect

If you own a development-suitable property, the typical sequence is:

1. **Direct approach** from one or more developers (60-70% of vendors get this first)
2. Vendor (often) signs an exclusive negotiation with that single developer
3. Negotiation produces a single-buyer offer, usually below market

A better sequence:

1. Direct approach received
2. Vendor declines to engage on price; engages a specialist advisor
3. Advisor runs a confidential EOI campaign across 8-15 buyers
4. Best of 5-15 offers selected
5. Sale 10-25% above the original direct approach

The difference is real money — typically $200k-$2m+ on premium development sites.

## Frequently Asked Questions

**Q: How do I tell if a developer's direct approach is at fair value?**
You almost always can't, without benchmarking the market. Engage a specialist advisor for an indicative valuation before responding to any direct approach.

**Q: Should I sign exclusivity with the first developer who approaches me?**
Almost never. Exclusivity benefits the developer (locks you out of competitive offers) and harms the vendor (kills price discovery). If you grant exclusivity, demand a non-refundable engagement fee in exchange.

**Q: Are off-market sales better than on-market?**
For premium development sites, almost always yes — better confidentiality, better-qualified buyers, better outcomes. For generic suburban residential, on-market with broad reach often produces strong outcomes.

---

*ACRES runs confidential off-market EOI campaigns for development site vendors across Brisbane and SEQ. For a confidential discussion, contact our team at 07 3096 0542.*
    `,
    faq: [
      { q: 'How do I tell if a developer\'s direct approach is at fair value?', a: 'You almost always can\'t without benchmarking. Engage a specialist advisor for an indicative valuation before responding.' },
      { q: 'Should I sign exclusivity with the first developer who approaches?', a: 'Almost never. Exclusivity locks out competition. If granting exclusivity, demand a non-refundable engagement fee.' },
      { q: 'Off-market or on-market for development sites?', a: 'For premium development sites, off-market almost always wins — better confidentiality, better-qualified buyers, better outcomes.' },
    ],
  },

  // ---------------------------------------------------------------- #156
  {
    id: 'can-developers-buy-multiple-houses-together',
    category: 'Quick Answer',
    title: 'Can Developers Buy Multiple Houses Together?',
    excerpt: 'Yes — and amalgamated sites are often worth 50-100% more than the sum of individual properties. Here\'s how site amalgamation works in Brisbane.',
    date: '2026-02-09',
    readTime: '6 min read',
    image: STREET_IMG,
    icon: Building2,
    metaDescription: 'Can property developers buy multiple houses together? Yes — site amalgamation in Brisbane explained, why amalgamated sites are worth more, and how the process works for landowners.',
    keyFacts: [
      'Site amalgamation — combining two or more adjoining properties — is a common Brisbane development tactic',
      'Amalgamated sites typically trade at 30-100% premiums over the sum of individual property values',
      'Amalgamation premium comes from height bonuses, design efficiency, and institutional-buyer access',
      'Most amalgamation transactions involve "domino" or "linked" contracts where each sale is conditional on the others',
      'ACRES has structured many multi-property amalgamations across Brisbane — contact 07 3096 0542',
    ],
    content: `
## The Short Answer

Yes — and it happens regularly across Brisbane. Two, three, or sometimes ten or more adjoining properties can be combined ("amalgamated") into a single development parcel. The amalgamated site is almost always worth more than the sum of its parts — typically 30-100% more, sometimes 150%+ for highly strategic assemblies.

## Why Amalgamated Sites Are Worth More

Three reasons amalgamated sites trade at premiums:

### 1. Higher Allowable Height
Some Brisbane neighbourhood plans grant additional height to larger sites. Common bonuses:
- 1,500+ sqm sites: +1 storey
- 2,500+ sqm sites: +2 storeys
- Sites achieving design excellence on amalgamated parcels: +20% additional height

A 1,000 sqm corner site limited to 5 storeys might trade at $4m. The same 1,000 sqm + an adjoining 1,200 sqm interior site amalgamated to 2,200 sqm might unlock 7 storeys — and trade at $8-9m combined, vs $4m + $3.5m = $7.5m standalone.

### 2. Better Design Efficiency
Larger sites support better building shapes, more efficient floor plates, more amenity, and lower per-square-metre construction costs. The dollar-per-square-metre value of amalgamated sites is structurally higher than smaller individual sites.

### 3. Institutional Buyer Access
Sites under 2,000 sqm interest small to mid-tier developers. Sites over 5,000 sqm can attract institutional capital (BTR platforms, super funds, REITs). Crossing the institutional threshold is a step-change in pricing.

## How the Mechanics Work

Site amalgamation transactions typically use one of three structures:

### Structure 1: Linked Contracts
Each owner signs a separate contract with the developer. Each contract is conditional on the others ("subject to all linked contracts becoming unconditional"). If any one owner withdraws, all contracts fall over.

This protects each owner — no one is committed unless everyone is committed. It also means the deal can fail at any link, which is why patient assemblies often take 12-24 months to complete.

### Structure 2: Sequential Options
The developer takes options on each property in turn. Each option is exercisable on completion of the assembly. The developer's risk is the cumulative non-refundable option fees if the assembly fails.

This protects the developer's position while parallel-running negotiations with each owner. Vendors receive option fees upfront.

### Structure 3: Single Master Contract
Less common, but used for very large assemblies. All owners sign a single master agreement that establishes the assembly and mechanics. Used in institutional or large-scale assemblies.

## Common Brisbane Examples

Site amalgamation is most common on:

- **Corner blocks with adjoining interior sites** (corner + interior creates a regular development parcel)
- **Multi-block runs along main roads** (e.g. four houses on a main-road corner)
- **Strategic parcels in active neighbourhood-plan corridors** (Coorparoo, Stones Corner, Albion, Woolloongabba)
- **Larger mixed-use precinct assemblies** (Fortitude Valley, South Brisbane)

## The Vendor's Strategic Question

If a developer approaches your property as part of an amalgamation:

1. **Are you the first or last to be approached?** First-approached owners often get lower prices. Last-approached can extract premium because the developer needs them to complete the assembly.

2. **Do you know if neighbours have been approached?** Information matters. Coordinated vendor positioning (with neighbour conversations) can strengthen each owner's negotiating position.

3. **Are you the linchpin?** A single owner can break or make an assembly. If your property is the linchpin (e.g., the corner site, or the only block on a key street), strategic premium is significantly higher.

ACRES routinely advises owners caught in active amalgamation processes — both early-approached and linchpin positions.

## What Could Go Wrong

Amalgamations are complex. Common failure modes:

- **One owner refuses to sell** — the assembly collapses
- **Pricing creep** — late owners demand premiums that destroy feasibility
- **Approval risk** — council refuses the assembled-yield application
- **Settlement risk** — financing issues at one site can cascade

For vendors, the risk is signing a linked contract that takes 12-18 months to complete, only to have it fall over at the last minute. Mitigation: insist on:

- Definite outside dates for the assembly to become unconditional
- Non-refundable deposits or option fees for vendor protection
- Right to terminate if assembly hasn't completed by outside date

## Frequently Asked Questions

**Q: Can I propose amalgamation to my neighbours myself?**
Yes — and it's often the highest-leverage move a landowner can make. Coordinated vendor proposals can capture 50%+ more value than uncoordinated individual sales. ACRES often facilitates these conversations on behalf of multiple owners.

**Q: How is amalgamation premium split between owners?**
Typically pro-rata on land area, with adjustments for site quality (corner vs interior, frontage, etc.). Specialist advisors structure equitable splits.

**Q: Is amalgamation taxed differently?**
The transaction is generally taxed the same as any property sale — capital gains, stamp duty (on the buyer side), GST as applicable. Get specialist tax advice for your specific situation.

---

*ACRES has structured multiple Brisbane site amalgamations across Brisbane and SEQ. For confidential discussion of whether amalgamation could unlock value on your property, contact our team at 07 3096 0542.*
    `,
    faq: [
      { q: 'Can I propose amalgamation to my neighbours myself?', a: 'Yes — often the highest-leverage move a landowner can make. Coordinated vendor proposals can capture 50%+ more value than uncoordinated individual sales.' },
      { q: 'How is amalgamation premium split between owners?', a: 'Typically pro-rata on land area, with adjustments for site quality (corner, frontage, etc.). Specialist advisors structure equitable splits.' },
      { q: 'Is amalgamation taxed differently?', a: 'Generally taxed the same as any property sale. Get specialist tax advice for your specific situation.' },
    ],
  },

  // ---------------------------------------------------------------- #158
  {
    id: 'how-much-more-can-developers-pay',
    category: 'Quick Answer',
    title: 'How Much More Can Developers Pay Than Home Buyers?',
    excerpt: 'For development-suitable property, developers typically pay 30-150% more than the residential-buyer market. Here\'s why — and how to know what bracket your land falls into.',
    date: '2026-02-09',
    readTime: '6 min read',
    image: HERO_IMG,
    icon: TrendingUp,
    metaDescription: 'How much more can developers pay than home buyers? Brisbane development premium explained — typical multiples, what determines them, and how landowners can identify their position.',
    keyFacts: [
      'Developer premiums over residential market range from 0% (no development potential) to 150%+ (institutional-grade strategic sites)',
      'For a typical Brisbane LMR-zoned 600 sqm site, developer premium is usually 30-60% above residential value',
      'For MU1-zoned inner-city sites, developer premium can reach 100-150%+',
      'The premium reflects what can be built (zoning) plus strategic factors (amalgamation, infrastructure, scarcity)',
      'ACRES provides free indicative valuations to identify your developer-vs-residential gap — visit acres.au/valuation',
    ],
    content: `
## The Short Answer

For property without development potential, developers pay residential-market rates — sometimes less, because they don't value the existing improvements (kitchens, landscaping). For property *with* development potential, developers can pay 30-150%+ more than the residential-buyer market.

The key determinant is what can legally be built on your land — and how much that built value exceeds your home's residential value.

## The Mathematics of the Premium

Developers pay based on **land value to a developer** (Residual Land Value), not on residential comparable sales. The two values can be wildly different.

A simple example. Suppose you own a 1,000 sqm Brisbane block with a 1970s house worth $1.4m on the residential market.

If your block is zoned **Low Density Residential** (no development potential), a developer pays roughly $1.0-$1.2m — actually *less* than residential, because they're demolishing the house.

If your block is zoned **LMR** (3 storeys, 5-8 townhouses possible), a developer might pay $1.8-$2.5m — 30-80% more than residential.

If your block is zoned **MU1** (up to 21m height, 25-35 apartments possible), a developer might pay $4-7m — 200-400% more than residential.

Same dirt, same address. The developer-vs-residential premium is dictated by zoning.

## What Drives The Premium Range

Five factors determine where in the 30-150% range your specific property sits:

### 1. Zoning Density
The single biggest driver. Higher density = higher premium. (See Quick Answer: [What Makes Land More Valuable to Developers?](/insights/what-makes-land-more-valuable-to-developers).)

### 2. Site Size
Sites at the institutional sweet spot (1,500-5,000 sqm) command higher premiums than sub-optimal sizes.

### 3. Site Shape and Frontage
Regular rectangular sites with wide frontage attract premium pricing; irregular sites discount.

### 4. Strategic Location
Within 800m of Cross River Rail, Olympic venues, or other infrastructure: 30-60% premium uplift on top of base development value.

### 5. Amalgamation Potential
Sites that can be combined with neighbours: 30-100%+ amalgamation premium on top of standalone development value.

## Quick Diagnostic

Use this rough table for an initial sense of where your Brisbane property sits:

| Your Zoning | Lot Size | Developer Premium vs Residential |
|---|---|---|
| Low Density Residential | Any | -10% to +10% |
| LMR (Low Medium Density) | <450 sqm | 0-30% |
| LMR | 450-700 sqm | 20-50% |
| LMR | 700-1,500 sqm | 30-80% |
| MDR (Medium Density) | 700-1,500 sqm | 50-120% |
| MDR | 1,500+ sqm | 80-150% |
| MU1 (Mixed Use) | 700-1,500 sqm | 100-200% |
| MU1 | 1,500+ sqm | 150-300% |

These are indicative — the actual premium depends on the specific site, market conditions, and competitive tension during sale. ACRES provides specific valuations.

## Why Many Owners Underestimate The Premium

Three reasons owners often undervalue their development-suitable property:

1. **Anchoring on residential comparables**: looking at house sales on the same street rather than at land-value-to-developer benchmarks
2. **Single-developer negotiation**: accepting the first developer's bid without testing the market
3. **Information asymmetry**: developers know feasibility; owners often don't

A specialist advisor closes the information gap and ensures the premium is captured.

## When There Is No Premium

Some properties have no developer premium — typically:

- Properties in pure low-density areas with no rezoning prospect
- Heritage-listed homes (development restricted)
- Severely constrained sites (flood, contamination, easement-burdened)
- Areas with no current developer interest

For these, residential-market sale is usually the best outcome.

## How to Identify Your Premium

Three steps:

1. **Check your zoning** at [eplan.brisbane.qld.gov.au](https://eplan.brisbane.qld.gov.au)
2. **Get an indicative valuation** at [acres.au/valuation](/valuation) — free, 60-second automated estimate or 24-48 hour specialist review
3. **For sites with material premium**, run a specialist EOI process — the difference between residential and developer outcomes is usually $200k-$2m+

## Frequently Asked Questions

**Q: How do I know if my property has development potential?**
Easiest test: zoning. If you're in LMR, MDR, MU1, or higher density residential, you almost certainly have development potential. If you're in low-density only, probably not.

**Q: Should I sell to a developer or to a home buyer?**
If the developer premium is 20%+, almost always sell to a developer — but only via a competitive process that captures the full premium. Single-developer negotiations often leave 30-50% of the premium on the table.

**Q: Will a residential agent know my developer premium?**
Most generic residential agents don't track development pricing. Specialist development advisors (like ACRES) maintain dedicated comparable databases.

---

*ACRES specialises in identifying and capturing developer premium across Brisbane and SEQ. For a confidential review of your property's developer-vs-residential gap, contact our team at 07 3096 0542 or visit acres.au/valuation.*
    `,
    faq: [
      { q: 'How do I know if my property has development potential?', a: 'Easiest test: zoning. LMR, MDR, MU1, or higher = almost certainly has development potential. Low-density only = probably not.' },
      { q: 'Should I sell to a developer or a home buyer?', a: 'If developer premium is 20%+, almost always developer — but only via competitive process. Single-developer negotiations leave 30-50% of premium on the table.' },
      { q: 'Will a residential agent know my developer premium?', a: 'Most generic residential agents don\'t track development pricing. Specialist development advisors maintain dedicated comparable databases.' },
    ],
  },

  // ---------------------------------------------------------------- #160
  {
    id: 'should-i-sell-before-the-olympics',
    category: 'Quick Answer',
    title: 'Should I Sell Before the Olympics?',
    excerpt: 'The 2032 Brisbane Olympics is already pricing into property values. Whether to sell before, during, or after depends on your situation, suburb, and whether you need cash or upside.',
    date: '2026-02-09',
    readTime: '7 min read',
    image: SITE_IMG,
    icon: TrendingUp,
    metaDescription: 'Should I sell my property before the 2032 Brisbane Olympics? When Olympic-driven property uplift peaks, suburb-specific impact, and how to decide based on your situation.',
    keyFacts: [
      '2032 Brisbane Olympic infrastructure investment is estimated at $19+ billion across venues, transport, and precinct upgrades',
      'Olympic-driven property uplift typically peaks 6-18 months before the Games — meaning 2030-2031 for Brisbane',
      'Affected suburbs (Woolloongabba, Albion, Hamilton, Brisbane CBD) have already seen 40-80% appreciation since 2020',
      'Selling before the Olympics is usually optimal for vendors needing cash; holding through the Games suits vendors with optionality',
      'ACRES tracks Olympic-driven uplift at the suburb level — contact 07 3096 0542',
    ],
    content: `
## The Short Answer

The 2032 Brisbane Olympics is the largest infrastructure event in modern Australian property history — $19+ billion of related investment. Olympic-driven property uplift is real, suburb-specific, and already partially priced into the market.

For vendors in directly affected suburbs (Woolloongabba, Albion, Hamilton, Brisbane CBD fringe), the question of "sell before or after" is genuinely strategic. The right answer depends on your suburb, your situation, and whether you need cash now or have flexibility for upside.

## How Olympic Uplift Works

Olympic-driven property appreciation happens in three phases:

### Phase 1: Announcement & Planning (2021-2025)
The Olympics was confirmed in July 2021. Affected suburbs began appreciating immediately as developers, investors, and capital allocators repositioned. Woolloongabba and Albion in particular saw 30-50% land-value appreciation through 2022-2024.

This phase is largely complete. The gain in *first-mover* premium has been captured.

### Phase 2: Infrastructure Build-Out (2025-2031)
Cross River Rail (opening 2026), Olympic-venue construction, and precinct upgrades visibly transform affected suburbs. Property values continue to appreciate but at a moderating pace as the headline-uplift gets priced in.

This phase is currently underway.

### Phase 3: Games & Legacy (2032-2035)
Games-time visibility creates short-term spike. Post-Games, infrastructure dividends continue to compound but at a normal-market pace.

For most affected suburbs, the curve looks like:

> 2020: baseline → 2024: +30-50% → 2027: +50-70% → 2030: +60-90% (peak) → 2035: +70-100% (settled)

Specific numbers vary by suburb. Woolloongabba and Hamilton are toward the higher end; suburbs further from venues see smaller absolute gains.

## When Uplift Peaks

A consistent finding across host cities (Sydney 2000, Vancouver 2010, London 2012, Tokyo 2020, Paris 2024): Olympic-driven property uplift peaks **6-18 months before the Games**, not during or after.

Why? The market prices in expected uplift well ahead. By the time the Games arrive, the news is fully absorbed and incremental upside is exhausted.

For Brisbane 2032, this implies the uplift peak is roughly **2030-2031**.

## Suburb-Specific Impact

Olympic uplift is concentrated in specific suburbs:

| Tier | Suburbs | Expected Total Uplift (2020-2032) |
|---|---|---|
| **Tier 1 — direct venue / precinct** | Woolloongabba, Albion, Hamilton, Brisbane CBD | 60-100% |
| **Tier 2 — adjacent / well-connected** | South Brisbane, Bowen Hills, Newstead, East Brisbane | 40-70% |
| **Tier 3 — broader catchment** | Greenslopes, Kelvin Grove, Coorparoo, West End | 25-50% |
| **Tier 4 — Brisbane wide** | Most other Brisbane suburbs | 15-35% |
| **Tier 5 — outside Brisbane** | Logan, Ipswich, Moreton Bay | 10-25% |

The Tier 1-2 uplift is meaningfully Olympic-driven; Tier 4-5 reflects general Brisbane market growth that would happen regardless of the Games.

## Should I Sell Before the Olympics?

The answer depends on three factors:

### 1. Your Cash Needs
- **Need cash now (downsizing, divorce, debt, retirement funding)**: sell now or in next 12-24 months. Don't wait — current pricing is strong, future timing is uncertain.
- **No cash needs, optionality preferred**: consider holding through 2030-2031 to capture peak uplift.

### 2. Your Suburb Tier
- **Tier 1-2 (direct Olympic impact)**: holding through 2030-2031 likely captures meaningful additional uplift.
- **Tier 4-5 (limited Olympic impact)**: timing is less critical; standard market considerations apply.

### 3. Your Property Type
- **Development-suitable land**: pricing is increasingly competitive; selling 2025-2027 captures both current premium and developer interest in pre-Olympic delivery.
- **Premium residential / lifestyle**: holding to 2030-2031 captures Olympic visibility premium.
- **Generic residential**: standard market timing — Olympic effect is modest.

## Hybrid Strategies

For owners who can't fully commit to either side, hybrid options exist:

### Long-Settlement Contract
Sell now at locked-in price, but settle 24-36 months later. You capture today's strong pricing with locked-in price protection, and have time to plan your next move. (See [How Long Settlements Increase Development Site Value](/insights/how-long-settlements-increase-development-site-value).)

### Put-and-Call Option
Receive a non-refundable option fee today; the developer can call the property over 24-36 months. You capture optionality on Olympic-driven appreciation while receiving cash today.

### Rent-Back After Sale
Sell now at strong pricing, lease the property back from the new owner for 12-36 months. You capture today's price while retaining occupancy through the Olympic build-up.

These hybrid structures often beat both "sell now" and "hold and wait".

## What ACRES Recommends

Our framework for advising vendors on Olympic timing:

1. **Get a specific suburb-tier valuation** — generic Brisbane forecasts are too coarse for this decision
2. **Run a hybrid-structure analysis** — long settlements and options often beat outright sell-now or hold-through
3. **Stress-test for downside scenarios** — what if interest rates spike, construction fails to deliver, or Games are scaled back?
4. **Match strategy to personal circumstances** — there is no universally optimal answer; the right move depends on your situation

## Frequently Asked Questions

**Q: What if the Games are delayed or cancelled?**
The Olympic premium is already partially priced. A delay would compress timing but not eliminate uplift; cancellation would partially reverse Tier 1-2 uplift but Tier 3-5 effects (which reflect general Brisbane growth) would remain.

**Q: Will the Olympic uplift continue post-2032?**
The infrastructure dividend (Cross River Rail, venues becoming community assets, transport upgrades) compounds permanently. The "Games premium" specifically peaks 2030-2031 and partially recedes post-Games.

**Q: Is it too late to capture Olympic uplift?**
For owners in Tier 1-2 suburbs: no. Meaningful uplift remains 2025-2031. For Tier 3-5: most of the Olympic-specific premium is already priced; remaining gains reflect general market growth.

---

*ACRES tracks Olympic-driven property impact at the suburb level across Brisbane. For confidential discussion of timing strategy on your property, contact our team at 07 3096 0542.*
    `,
    faq: [
      { q: 'What if the Games are delayed or cancelled?', a: 'Olympic premium already partially priced. Delay would compress timing not eliminate uplift; cancellation would partially reverse Tier 1-2 gains but Tier 3-5 effects (general growth) remain.' },
      { q: 'Will Olympic uplift continue post-2032?', a: 'Infrastructure dividend compounds permanently. "Games premium" peaks 2030-2031 and partially recedes post-Games.' },
      { q: 'Is it too late to capture Olympic uplift?', a: 'For Tier 1-2 suburbs: no — meaningful uplift remains 2025-2031. Tier 3-5: most Olympic-specific premium already priced.' },
    ],
  },
];
